import { type FC } from 'react';
import LabelIcon from '@mui/icons-material/Label';
import { Box, Tooltip, Typography } from '@mui/material';

import { getLabelColor } from '../../pastelPalette';
import type { AppointmentLabel } from '../../types';

export interface LabelIconRowProps {
  /** Labels assigned to the appointment (from `appointment.labels[]`). */
  labels: AppointmentLabel[];
  /** Max icons before collapsing the remainder into a "+N" affordance. */
  max?: number;
  /** Icon size in px. */
  size?: number;
}

/**
 * Renders an appointment's labels as a compact row of filled, pastel-tinted
 * label icons for the calendar event block. The encounter-type ring remains the
 * dominant color; these are a secondary signal. Overflows to "+N" when the icon
 * area (shared with telehealth/status icons) runs out of room.
 */
export const LabelIconRow: FC<LabelIconRowProps> = ({ labels, max = 3, size = 14 }) => {
  if (!labels || labels.length === 0) {
    return null;
  }

  const shown = labels.slice(0, max);
  const overflow = labels.length - shown.length;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        flexShrink: 0,
      }}
    >
      {shown.map(label => (
        <Tooltip key={label.label_id} title={label.name} arrow>
          <LabelIcon
            aria-label={label.name}
            sx={{ fontSize: size, color: getLabelColor(label.color) }}
          />
        </Tooltip>
      ))}
      {overflow > 0 && (
        <Typography
          component="span"
          aria-label={`${overflow} more labels`}
          sx={{
            fontSize: size - 3,
            fontWeight: 600,
            color: 'text.secondary',
            lineHeight: 1,
          }}
        >
          +{overflow}
        </Typography>
      )}
    </Box>
  );
};
